import { Accordion, Button, Group, Text, ThemeIcon } from '@mantine/core';
import { IconArrowsSplit, IconGitMerge, IconLink } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { formatCurrency, TenantCurrency } from 'src/common/currency';
import type { MatchSuggestion } from '../../types';
import SuggestionReviewTable from './SuggestionReviewTable';
import { classifyGroup, groupSuggestions, type GroupShape } from './groupSuggestions';
import { useCurrency } from 'src/common/hooks/useCurrency';

type AmbiguousGroupCardsProps = {
  suggestions: MatchSuggestion[];
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
  onUnlink: (id: string) => void;
  onReopen: (id: string) => void;
  onRejectGroup: (ids: string[]) => void;
  onBulkConfirm?: (ids: string[]) => Promise<void>;
  onBulkReject?: (ids: string[]) => Promise<void>;
  onBulkUnlink?: (ids: string[]) => Promise<void>;
  onBulkReopen?: (ids: string[]) => Promise<void>;
};

function formatValue(value: number | null, currency: TenantCurrency): string {
  if (value == null) return '—';
  return formatCurrency(value, currency, { fractionDigits: 0 }) ?? '—';
}

function GroupIcon({ shape }: { shape: GroupShape }) {
  const icons: Record<GroupShape, ReactNode> = {
    'fan-out': <IconArrowsSplit size={16} />,
    'fan-in': <IconGitMerge size={16} />,
    complex: <IconLink size={16} />,
  };
  const icon = icons[shape];

  return (
    <ThemeIcon variant="light" size="sm" color="gray">
      {icon}
    </ThemeIcon>
  );
}

function GroupHeader({ group, shape }: { group: MatchSuggestion[]; shape: GroupShape }) {
  const currency = useCurrency();
  if (shape === 'fan-out') {
    const feed = group[0].unprocessedProduct;
    return (
      <Group gap="sm">
        <GroupIcon shape={shape} />
        <div>
          <Text size="sm" fw={500}>
            {feed.providerReferenceNumber}
          </Text>
          <Text size="xs" c="dimmed">
            {feed.productName} · {formatValue(feed.value, currency)} · {group.length} possible
            assets
          </Text>
        </div>
      </Group>
    );
  }

  if (shape === 'fan-in') {
    const asset = group[0].suggestedAsset;
    return (
      <Group gap="sm">
        <GroupIcon shape={shape} />
        <div>
          <Text size="sm" fw={500}>
            {asset.providerReferenceNumber} on {asset.clientName}
          </Text>
          <Text size="xs" c="dimmed">
            {asset.description} · {formatValue(asset.value, currency)} · {group.length} possible
            feed products
          </Text>
        </div>
      </Group>
    );
  }

  const uniqueFeeds = new Set(group.map(s => s.unprocessedProduct.id));
  const uniqueAssets = new Set(group.map(s => s.suggestedAsset.id));
  return (
    <Group gap="sm">
      <GroupIcon shape={shape} />
      <div>
        <Text size="sm" fw={500}>
          {uniqueFeeds.size} feed products, {uniqueAssets.size} assets
        </Text>
        <Text size="xs" c="dimmed">
          Linked through shared references · {group.length} suggestions
        </Text>
      </div>
    </Group>
  );
}

export default function AmbiguousGroupCards({
  suggestions,
  onConfirm,
  onReject,
  onUnlink,
  onReopen,
  onRejectGroup,
  onBulkConfirm,
  onBulkReject,
  onBulkUnlink,
  onBulkReopen,
}: AmbiguousGroupCardsProps) {
  const groups = groupSuggestions(suggestions);

  if (groups.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="xl">
        No ambiguous matches for this provider.
      </Text>
    );
  }

  return (
    <Accordion variant="cells">
      {groups.map(group => {
        const shape = classifyGroup(group);
        return (
          <Accordion.Item key={group[0].id} value={group[0].id} role="region">
            <Accordion.Control>
              <GroupHeader group={group} shape={shape} />
            </Accordion.Control>
            <Accordion.Panel>
              <SuggestionReviewTable
                suggestions={group}
                showStatusFilter={false}
                headerAction={
                  <Button
                    variant="subtle"
                    size="compact-xs"
                    color="red"
                    onClick={() => onRejectGroup(group.map(s => s.id))}
                  >
                    None of these
                  </Button>
                }
                onConfirm={onConfirm}
                onReject={onReject}
                onUnlink={onUnlink}
                onReopen={onReopen}
                onBulkConfirm={onBulkConfirm}
                onBulkReject={onBulkReject}
                onBulkUnlink={onBulkUnlink}
                onBulkReopen={onBulkReopen}
              />
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}
